import { useCurrentUser, useDateHelper, useNavigationHelper } from 'FrontRoyalAngular';
import { ProgramTypeConfigs } from 'Program';
import { memo, useCallback, useMemo } from 'react';
import { getAdmissionOffer, getCohort } from 'Users';
import { SidebarBoxKey } from 'AdmissionsGuidance';
import { useTranslation } from 'react-i18next';
import { getRegistrationDeadline } from 'AdmissionOffer';
import { BasicMobileBox } from '../../BasicMobileBox';
import { ImportantCtaBox } from '../../ImportantCtaBox';

function RegisterForProgramBoxComponent({ mobile }: { mobile?: boolean }) {
    const currentUser = useCurrentUser();
    const { loadRoute } = useNavigationHelper();
    const dateHelper = useDateHelper();
    const cohort = useMemo(() => currentUser && getCohort(currentUser), [currentUser]);
    const admissionOffer = useMemo(() => getAdmissionOffer(currentUser), [currentUser]);

    const registrationDeadline = useMemo(() => {
        if (!admissionOffer || !cohort) return null;
        const _registrationDeadline = getRegistrationDeadline(admissionOffer, cohort);
        if (!_registrationDeadline) return null;
        return dateHelper.formattedUserFacingMonthDayLong(_registrationDeadline);
    }, [admissionOffer, dateHelper, cohort]);

    const programTitle = useMemo(() => {
        if (!cohort) return null;
        const config = ProgramTypeConfigs[cohort.programType];
        return config.programBoxTitle?.(cohort) ?? config.fullTitle(cohort);
    }, [cohort]);
    const shortProgramTitle = useMemo(() => programTitle?.replace(/^The\s/, ''), [programTitle]);

    const onCtaPress = useCallback(() => {
        loadRoute('/settings/application_status');
    }, [loadRoute]);

    if (!programTitle || !shortProgramTitle || !registrationDeadline) return null;

    return mobile ? (
        <RegisterForProgramBoxMobilePresentation programTitle={programTitle} onCtaPress={onCtaPress} />
    ) : (
        <RegisterForProgramBoxPresentation
            programTitle={programTitle}
            shortProgramTitle={shortProgramTitle}
            onCtaPress={onCtaPress}
            registrationDeadline={registrationDeadline}
        />
    );
}

function RegisterForProgramBoxPresentation({
    programTitle,
    shortProgramTitle,
    onCtaPress,
    registrationDeadline,
}: {
    programTitle: string;
    shortProgramTitle: string;
    onCtaPress: () => void;
    registrationDeadline: string;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'registerForProgramBox' });
    return (
        <ImportantCtaBox
            headerSubtitle={t('subtitle')}
            headerTitle={programTitle}
            boxKey={SidebarBoxKey.register_for_program}
            ctaText={t('ctaText')!}
            onPressCta={onCtaPress}
        >
            {t('content', { programTitle: shortProgramTitle, registrationDeadline })}
        </ImportantCtaBox>
    );
}

function RegisterForProgramBoxMobilePresentation({
    programTitle,
    onCtaPress,
}: {
    programTitle: string;
    onCtaPress: () => void;
}) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'registerForProgramBox' });
    return (
        <BasicMobileBox
            heading={programTitle}
            boxKey={SidebarBoxKey.register_for_program}
            ctaText={t('ctaText')!}
            onPress={onCtaPress}
            buttonId="cta"
        />
    );
}

export const RegisterForProgramBox = memo(RegisterForProgramBoxComponent);
